<template>
  <!-- Full-screen on mobile; normal inline flow on sm+ -->
  <div
    class="fixed inset-0 z-50 flex flex-col bg-white p-4 overflow-y-auto sm:static sm:z-auto sm:p-0 sm:overflow-visible sm:h-full w-full"
  >
    <div v-if="isLoading" class="flex flex-col flex-auto justify-center text-center">
      <LayupSpinner />
      <p class="text-black">Loading...</p>
    </div>
    <div v-else class="flex flex-col text-left divide-y h-full w-full">
      <div class="flex cursor-pointer mb-3" @click="back">
        <img src="@/assets/icons/backArrow.svg" />
        <div class="self-center text-center text-lg font-semibold grow text-black">Checkout</div>
      </div>
      <div
        v-if="errorResponse.message || errorResponse.title"
        class="flex flex-col flex-auto justify-center text-center pt-3"
      >
        <p class="text-black text-lg font-bold">{{ errorResponse.title }}</p>
        <ErrorDisplay :errorMessage="errorResponse.message" showBackButton />
      </div>
      <div v-else-if="shouldShowPeach" class="grow py-1 mb-2">
        <div id="payment-form" :class="{ 'h-[500px]': checkoutFormLoaded }"></div>
      </div>
      <div class="flex flex-row py-2 mt-auto">
        <div class="flex flex-1 w-auto">
          <p class="text-extrabold text-base-black text-sm mr-1">Amount Due</p>
          <div class="mt-0.5">
            <ToolTip
              :text="'The amount due contributes towards or secures your product for the duration of the lay-by agreement.'"
            />
          </div>
        </div>
        <div class="font-bold text-base-black">
          {{ amountDisplay }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import ToolTip from '@/components/common/ToolTip.vue';
import { usePeachCheckout } from '@/composables/usePeachCheckout';
import { useWidgetStore } from '@/stores/widget';
import { displayCurrency } from '@/utils/format.utils';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import ErrorDisplay from '../../common/ErrorDisplay.vue';

const widgetStore = useWidgetStore();
const router = useRouter();

const paymentMethod = router.currentRoute.value.query.paymentMethod as string;
const amountDisplay = displayCurrency(widgetStore.amountDue);

const shouldShowPeach = computed(
  () =>
    (paymentMethod === 'CARD' && widgetStore.order.paymentMethods.creditOrDebitCard) ||
    (paymentMethod === 'EFT' && widgetStore.order.paymentMethods.eft),
);

const { isLoading, errorResponse, checkoutFormLoaded, initiate } = usePeachCheckout({
  formElementId: 'payment-form',
  channel: 'widget',
  onCompleted: (paymentId) => router.replace({ name: 'paymentStatus', params: { id: paymentId } }),
  onCancelled: () => router.replace({ name: 'SelectPaymentMethod' }),
});

const back = () => {
  const prev = window.history.state?.back as string | undefined;
  if (prev && prev.includes('paymentMethod')) {
    router.replace({ name: 'SelectPaymentMethod' });
  } else {
    router.back();
  }
};

const init = async () => {
  if (!shouldShowPeach.value) {
    return router.replace({ name: 'SelectPaymentMethod' });
  }
  await initiate(widgetStore.id, widgetStore.amountDue, paymentMethod);
};

init();
</script>
